import { useRef, useState } from "react";
import { ChevronDown, ChevronRight, LayoutGrid } from "lucide-react";
import { sectionsFromTree } from "@/constants/catalogCategories";

function slug(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function shortLabel(label) {
  const text = String(label || "").replace(/\s+/g, " ").trim();
  return text.length > 36 ? `${text.slice(0, 34)}…` : text;
}

export function categorySections(tree) {
  return sectionsFromTree(tree).filter((section) => section.count);
}

export function menuColumns(section) {
  if (!section) return [];
  if (section.kind !== "department") {
    return [{
      key: section.id,
      label: section.label,
      shop: { category: section.id },
      links: (section.children || []).filter((row) => row.count).slice(0, 8).map((sub) => ({
        key: sub.id,
        label: sub.label,
        shop: { category: section.id, sub: sub.id },
      })),
    }];
  }
  return [...(section.children || [])]
    .filter((row) => row.count)
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 8)
    .map((child) => ({
      key: child.id,
      label: child.label,
      shop: { dept: section.id, category: child.id },
      links: (child.children || [])
        .filter((sub) => sub.count && String(sub.id).toLowerCase() !== "other")
        .slice(0, 7)
        .map((sub) => ({
          key: sub.id,
          label: sub.label,
          shop: { dept: section.id, category: child.id, sub: sub.id },
        })),
    }));
}

function shopTarget(section) {
  return section?.kind === "department" ? { dept: section.id } : { category: section.id };
}

export function CategoryMenu({ tree, onShop, linkClass }) {
  const sections = categorySections(tree);
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState("");
  const hideTimer = useRef(null);
  const pinned = useRef(false);
  const active = sections.find((section) => section.id === hoverId) || sections[0];
  const columns = menuColumns(active);
  const show = () => {
    clearTimeout(hideTimer.current);
    setOpen(true);
  };
  const hide = () => {
    hideTimer.current = setTimeout(() => {
      if (!pinned.current) setOpen(false);
    }, 220);
  };

  const choose = (shop) => {
    pinned.current = false;
    setOpen(false);
    onShop(shop);
  };

  return (
    <div onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        data-testid="nav-categories"
        aria-expanded={open ? "true" : "false"}
        onClick={() => {
          pinned.current = !pinned.current;
          setOpen(pinned.current);
        }}
        className={`${linkClass} outline-none`}
      >
        <LayoutGrid className="w-4 h-4" />
        Categories
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && sections.length > 0 ? (
        <div
          className="absolute left-0 right-0 top-full z-50"
          data-testid="category-menu"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className="flex max-h-[70vh] w-full max-w-[640px] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            <div className="w-[180px] shrink-0 overflow-y-auto border-r border-border p-2">
              {sections.map((section) => {
                const selected = active?.id === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onMouseEnter={() => setHoverId(section.id)}
                    onClick={() => choose(shopTarget(section))}
                    data-testid={`nav-${slug(section.id)}`}
                    className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm font-medium ${
                      selected ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    <span className="min-w-0 truncate">{section.label}</span>
                    {selected ? <ChevronRight className="h-4 w-4 shrink-0" /> : null}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => choose({})}
                data-testid="nav-shop-all"
                className="mt-1 w-full rounded-sm px-2 py-1.5 text-left text-sm font-medium text-aura-muted hover:bg-accent hover:text-accent-foreground"
              >
                New arrivals
              </button>
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto p-3">
              {active ? (
                <button
                  type="button"
                  onClick={() => choose(shopTarget(active))}
                  className="mb-3 px-2 text-left text-[10px] tracking-luxe uppercase text-aura-muted hover:text-aura-ink"
                >
                  Shop all {active.label}
                </button>
              ) : null}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {columns.map((column) => (
                  <div key={column.key}>
                    <button
                      type="button"
                      title={column.label}
                      onClick={() => choose(column.shop)}
                      data-testid={`nav-${slug(active?.id)}-${slug(column.key)}`}
                      className="mb-1 block max-w-full truncate px-2 text-left text-[10px] tracking-luxe uppercase text-aura-muted hover:text-aura-ink"
                    >
                      {shortLabel(column.label)}
                    </button>
                    <ul>
                      {column.links.map((link) => (
                        <li key={link.key}>
                          <button
                            type="button"
                            title={link.label}
                            onClick={() => choose(link.shop)}
                            className="block w-full max-w-full truncate rounded-sm px-2 py-1.5 text-left text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                          >
                            {shortLabel(link.label)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function MobileCategories({ tree, onShop }) {
  const sections = categorySections(tree);
  const [openId, setOpenId] = useState("");
  return (
    <div>
      <div className="text-[10px] tracking-luxe uppercase text-aura-muted mt-3">Categories</div>
      <button type="button" onClick={() => onShop({})} className="text-sm text-left py-0.5" data-testid="mobile-nav-shop">
        New arrivals
      </button>
      {sections.map((section) => {
        const open = openId === section.id;
        const columns = menuColumns(section).slice(0, 8);
        return (
          <div key={section.id}>
            <button
              type="button"
              onClick={() => setOpenId(open ? "" : section.id)}
              className="flex w-full items-center justify-between py-1 text-left text-sm font-medium"
              data-testid={`mobile-nav-${slug(section.id)}`}
            >
              {section.label}
              <ChevronDown className={`h-3.5 w-3.5 opacity-60 ${open ? "rotate-180" : ""}`} />
            </button>
            {open ? (
              <div className="mb-2 ml-3 border-l border-white/10 pl-3">
                <button
                  type="button"
                  onClick={() => onShop(shopTarget(section))}
                  className="block py-0.5 text-left text-sm text-aura-ink/80"
                >
                  Shop all {section.label}
                </button>
                {columns.map((column) => (
                  <div key={column.key} className="mt-1">
                    <button
                      type="button"
                      onClick={() => onShop(column.shop)}
                      className="block py-0.5 text-left text-sm text-aura-ink/80"
                    >
                      {shortLabel(column.label)}
                    </button>
                    {column.links.slice(0, 4).map((link) => (
                      <button
                        key={link.key}
                        type="button"
                        onClick={() => onShop(link.shop)}
                        className="block py-0.5 pl-3 text-left text-[13px] text-aura-ink/60"
                      >
                        {shortLabel(link.label)}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
